import React from 'react';
import { motion } from 'framer-motion';

interface ButtonProps {
  children: React.ReactNode;
  variant?: 'primary' | 'ghost' | 'outline';
  className?: string;
  onClick?: () => void;
  type?: 'button' | 'submit' | 'reset';
  disabled?: boolean;
}

export const Button: React.FC<ButtonProps> = ({
  children,
  variant = 'primary',
  className = '',
  onClick,
  type = 'button',
  disabled = false,
}) => {
  const baseStyles = "relative px-6 py-2.5 rounded-lg font-medium transition-colors duration-300 text-sm tracking-wide flex items-center justify-center gap-2 overflow-hidden";
  
  const variants = {
    primary: "bg-[#0ea5e9] text-white hover:bg-[#0284c7] shadow-[0_0_15px_rgba(14,165,233,0.3)] border border-transparent",
    ghost: "bg-transparent text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white hover:bg-slate-200 dark:hover:bg-white/5",
    outline: "border border-[#0ea5e9] text-[#0ea5e9] hover:bg-[#0ea5e9]/10"
  };

  const getMotionProps = () => {
    if (variant === 'primary') {
      return {
        whileHover: { 
          scale: 1.02,
          boxShadow: [
            "0 0 15px rgba(14,165,233,0.3)",
            "0 0 25px rgba(14,165,233,0.6)",
            "0 0 15px rgba(14,165,233,0.3)"
          ],
          transition: {
            boxShadow: {
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut" as const,
            },
            scale: { duration: 0.2 }
          }
        },
        whileTap: { scale: 0.98 }
      };
    }
    return {
      whileHover: { scale: 1.02 },
      whileTap: { scale: 0.98 }
    };
  };

  return (
    <motion.button
      {...getMotionProps()}
      className={`${baseStyles} ${variants[variant]} ${className} ${disabled ? 'opacity-50 cursor-not-allowed pointer-events-none' : ''}`}
      onClick={onClick}
      type={type}
      disabled={disabled}
    >
      <span className="relative z-10 flex items-center gap-2">{children}</span>
      
      {/* Subtle shine effect for primary buttons */}
      {variant === 'primary' && (
        <motion.div 
          className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent skew-x-[-20deg]"
          initial={{ x: '-150%' }}
          whileHover={{ x: '150%' }}
          transition={{ duration: 0.8, ease: "easeInOut" }}
        />
      )}
    </motion.button>
  );
};